import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import PaymentIcon from '@mui/icons-material/Payment';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarIcon from '@mui/icons-material/Star';
import BusinessIcon from '@mui/icons-material/Business';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  formatProjectDate,
  getProjectBucket,
  getProjectModeLabel,
  getProjectPrimaryAction,
  getProjectStatusLabel,
  getProjectTimestamp,
  isProjectReadyToDownload,
} from '../utils/projectState';

const WorkspaceHero = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #8B5CF6 100%)`,
  color: theme.palette.common.white,
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 'auto -10% -35% auto',
    width: 220,
    height: 220,
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
}));

const SummaryCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  height: '100%',
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const QuickActionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const ActivityCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [downloadingProjects, setDownloadingProjects] = useState(new Set());

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const authToken = window.localStorage.getItem('token');
      const response = await axios.get('/api/projects', {
        headers: {
          Authorization: ['Bearer', authToken].join(' '),
        },
      });

      setProjects(response.data.projects || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load your workspace. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Refresh user data once on mount to ensure credits/account info is current,
    // then load the projects list.
    // `refreshUser` is intentionally excluded from the dependency array.
    // Although it is now a stable useCallback reference in AuthContext, keeping
    // it out of the deps array makes it explicit that this effect runs exactly
    // once on mount regardless of any future changes to refreshUser's identity.
    refreshUser?.();
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshUser excluded intentionally (see comment above)
  }, [fetchDashboardData]);

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => new Date(getProjectTimestamp(b) || 0) - new Date(getProjectTimestamp(a) || 0)),
    [projects]
  );

  const summary = useMemo(() => {
    const counts = sortedProjects.reduce((acc, project) => {
      const bucket = getProjectBucket(project);
      acc[bucket] += 1;
      return acc;
    }, { inProgress: 0, actionNeeded: 0, completed: 0, other: 0 });

    return {
      inProgress: counts.inProgress,
      paymentRequired: sortedProjects.filter((project) => getProjectPrimaryAction(project).key === 'pay').length,
      readyToDownload: sortedProjects.filter(isProjectReadyToDownload).length,
      studioCredits: user?.themeCredits || 0,
    };
  }, [sortedProjects, user?.themeCredits]);

  const recentProjects = useMemo(() => sortedProjects.slice(0, 4), [sortedProjects]);

  const handleLogout = async () => {
    setActionLoading(true);
    await logout();
    navigate('/login');
  };

  const handleDownload = async (project) => {
    try {
      setDownloadingProjects((prev) => new Set([...prev, project.id]));

      const authToken = window.localStorage.getItem('token');
      const response = await axios.get(`/api/download/${project.uuid}`, {
        responseType: 'blob',
        headers: {
          Authorization: ['Bearer', authToken].join(' '),
        },
      });

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project.name || 'theme'}-${project.id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Dashboard download failed:', downloadError);
      setError(`Download failed: ${downloadError.response?.data?.error || downloadError.message}`);
    } finally {
      setDownloadingProjects((prev) => {
        const next = new Set(prev);
        next.delete(project.id);
        return next;
      });
    }
  };

  const getBucketChip = (project) => {
    const bucket = getProjectBucket(project);
    const chipProps = {
      inProgress: { label: 'In Progress', color: 'info' },
      actionNeeded: { label: 'Action Needed', color: 'warning' },
      completed: { label: 'Ready', color: 'success' },
      other: { label: 'Project', color: 'default' },
    };

    return chipProps[bucket] || chipProps.other;
  };

  const summaryCards = [
    {
      title: 'In Progress',
      value: summary.inProgress,
      subtitle: 'Themes currently generating or packaging',
      icon: <AccessTimeIcon color="info" />,
    },
    {
      title: 'Ready to Download',
      value: summary.readyToDownload,
      subtitle: 'Finished themes you can unlock or download now',
      icon: <CheckCircleIcon color="success" />,
    },
    {
      title: 'Payment Required',
      value: summary.paymentRequired,
      subtitle: 'Completed themes waiting to be unlocked',
      icon: <PaymentIcon color="warning" />,
    },
    {
      title: 'Studio Credits Remaining',
      value: summary.studioCredits,
      subtitle: summary.studioCredits > 0 ? 'Available for custom theme work' : 'Need more credits? See current pricing',
      icon: <StarIcon color="primary" />,
    },
  ];

  const quickActions = [
    {
      title: 'Quick Themes',
      description: 'Launch a template-based theme fast.',
      icon: <BoltIcon fontSize="large" />,
      to: '/quick-themes',
      color: 'success.main',
    },
    {
      title: 'Custom Themes',
      description: 'Start a more tailored business theme.',
      icon: <AutoAwesomeIcon fontSize="large" />,
      to: '/ai-custom',
      color: 'primary.main',
    },
    {
      title: 'Business Profiles',
      description: 'Reuse saved business details across projects.',
      icon: <BusinessIcon fontSize="large" />,
      to: '/profile/dashboard',
      color: 'warning.main',
    },
    {
      title: 'Pricing & Studio Credits',
      description: summary.studioCredits > 0
        ? `${summary.studioCredits} credit${summary.studioCredits === 1 ? '' : 's'} ready to use`
        : 'Buy credits or review one-time theme pricing.',
      icon: <StarIcon fontSize="large" />,
      to: '/pricing',
      color: 'info.main',
    },
  ];

  const renderPrimaryAction = (project) => {
    const action = getProjectPrimaryAction(project);

    if (action.key === 'download') {
      return (
        <Button
          variant="contained"
          color="success"
          size="small"
          startIcon={downloadingProjects.has(project.id) ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          onClick={() => handleDownload(project)}
          disabled={downloadingProjects.has(project.id)}
        >
          {downloadingProjects.has(project.id) ? 'Downloading...' : action.label}
        </Button>
      );
    }

    return (
      <Button
        component={RouterLink}
        to={`/projects/${project.id}`}
        variant={action.key === 'pay' ? 'contained' : 'outlined'}
        color={action.key === 'pay' ? 'primary' : 'inherit'}
        size="small"
        startIcon={action.key === 'resume' ? <AccessTimeIcon /> : action.key === 'pay' ? <PaymentIcon /> : <VisibilityIcon />}
      >
        {action.label}
      </Button>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <WorkspaceHero elevation={0}>
        <Grid container spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 1 }}>
              Workspace
            </Typography>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Welcome back, {user?.firstName || 'there'}.
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.92, mb: 3, maxWidth: 680 }}>
              Start a new theme, keep active generations moving, and unlock finished work from one clean home base.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to="/quick-themes"
                variant="contained"
                startIcon={<BoltIcon />}
                sx={{ bgcolor: 'common.white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
              >
                Quick Themes
              </Button>
              <Button
                component={RouterLink}
                to="/ai-custom"
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                sx={{ borderColor: 'common.white', color: 'common.white', '&:hover': { borderColor: 'common.white', bgcolor: 'rgba(255,255,255,0.08)' } }}
              >
                Custom Themes
              </Button>
              <Button
                component={RouterLink}
                to="/projects"
                variant="text"
                startIcon={<FolderOpenIcon />}
                sx={{ color: 'common.white' }}
              >
                Open Projects
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.12)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <Typography variant="subtitle2" sx={{ opacity: 0.85, mb: 1 }}>
                Today's focus
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                {summary.paymentRequired > 0
                  ? `${summary.paymentRequired} theme${summary.paymentRequired === 1 ? '' : 's'} ready to unlock`
                  : summary.inProgress > 0
                    ? `${summary.inProgress} active generation${summary.inProgress === 1 ? '' : 's'} in motion`
                    : 'Create your next WordPress theme'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {summary.readyToDownload > 0
                  ? `${summary.readyToDownload} completed theme${summary.readyToDownload === 1 ? '' : 's'} can be downloaded now.`
                  : 'Use Business Profiles to save time across future projects.'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </WorkspaceHero>

      {error && (
        <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mt: 1, mb: 2 }}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <SummaryCard elevation={0}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {card.title}
                </Typography>
                {card.icon}
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                {loading ? <CircularProgress size={28} /> : card.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.subtitle}
              </Typography>
            </SummaryCard>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Start or continue work
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Jump straight into the actions that matter most in the current pay-on-completion workflow.
        </Typography>
        <Grid container spacing={3}>
          {quickActions.map((action) => (
            <Grid item xs={12} sm={6} md={3} key={action.title}>
              <QuickActionCard component={RouterLink} to={action.to} elevation={0}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: action.color,
                    color: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  {action.icon}
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {action.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
              </QuickActionCard>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mt: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Recent activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A compact look at what needs your attention next.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh workspace">
              <IconButton onClick={fetchDashboardData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button component={RouterLink} to="/projects" variant="outlined">
              View All Projects
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
            <CircularProgress />
          </Box>
        ) : recentProjects.length > 0 ? (
          <Grid container spacing={2}>
            {recentProjects.map((project) => {
              const bucketChip = getBucketChip(project);
              return (
                <Grid item xs={12} md={6} key={project.id}>
                  <ActivityCard elevation={0}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                        <Box>
                          <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {project.name || `Project #${project.id}`}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label={getProjectModeLabel(project.mode)} size="small" variant="outlined" />
                            <Chip label={bucketChip.label} size="small" color={bucketChip.color} />
                            <Chip label={getProjectStatusLabel(project.status)} size="small" variant="outlined" />
                          </Box>
                        </Box>
                        {renderPrimaryAction(project)}
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">
                            Last updated
                          </Typography>
                          <Typography variant="body2">
                            {formatProjectDate(getProjectTimestamp(project))}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">
                            Next action
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getProjectPrimaryAction(project).key === 'pay' ? <PaymentIcon fontSize="small" color="primary" /> : null}
                            {getProjectPrimaryAction(project).key === 'download' ? <DownloadIcon fontSize="small" color="success" /> : null}
                            {getProjectPrimaryAction(project).key === 'resume' ? <AccessTimeIcon fontSize="small" color="info" /> : null}
                            {getProjectPrimaryAction(project).key === 'view' ? <WarningAmberIcon fontSize="small" color="warning" /> : null}
                            {project.message || getProjectPrimaryAction(project).label}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </ActivityCard>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Paper sx={{ p: 5, borderRadius: 3, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              No recent projects yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start with a Quick Theme or Custom Theme and your workspace will show the next steps here.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button component={RouterLink} to="/quick-themes" variant="contained" startIcon={<BoltIcon />}>
                Quick Themes
              </Button>
              <Button component={RouterLink} to="/ai-custom" variant="outlined" startIcon={<AutoAwesomeIcon />}>
                Custom Themes
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleLogout} color="error" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Logout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Dashboard;
